import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { Box } from '@fundamentals'
import PSPDFKit, { Instance } from 'pspdfkit'

type ComponentType = {
  document: string
  readOnly?: boolean
  onAnnotate?: (blob: Blob, url: string) => void
}

export interface PspdfkitComponentRef {
  setViewState: (callback: (viewState: any) => any) => void
  getInstance: () => Instance | null
  setSearchState: (searchState: { term: string }) => void
  clearSearchState: () => void
  nextSearchResult: () => void
  previousSearchResult: () => void
  getSearchResultCount: () => number
  getCurrentResultIndex: () => number
  getSearchInfo: () => {
    resultCount: number
    currentIndex: number
    hasResults: boolean
    term: string
  }
}

const PspdfkitComponent = forwardRef<PspdfkitComponentRef, ComponentType>(
  ({ document, readOnly = true, onAnnotate }, ref) => {
    const containerRef = useRef(null)
    const instanceRef = useRef<Instance | null>(null)
    const pspdfkitRef = useRef<typeof PSPDFKit | null>(null)

    useImperativeHandle(
      ref,
      () => {
        // Helper: Validate instance and get search state
        const getValidatedSearchState = () => {
          if (!instanceRef.current || !pspdfkitRef.current) return null
          const searchState = instanceRef.current.searchState
          if (!searchState.results || searchState.results.size === 0)
            return null
          return searchState
        }

        // Helper: Navigate to specific result index
        const navigateToResult = (targetIndex: number) => {
          const searchState = getValidatedSearchState()
          if (!searchState) return

          const newState = searchState.set('focusedResultIndex', targetIndex)
          instanceRef.current.setSearchState(newState)

          // Jump to result location
          const result = searchState.results.get(targetIndex)
          if (result.pageIndex !== null && result.rectsOnPage.size > 0) {
            instanceRef.current.jumpToRect(
              result.pageIndex,
              result.rectsOnPage.get(0),
            )
          }
        }

        return {
          setViewState: (callback) => {
            instanceRef.current?.setViewState(callback)
          },

          getInstance: () => instanceRef.current,

          setSearchState: async (searchState) => {
            if (instanceRef.current && pspdfkitRef.current) {
              const results = await instanceRef.current.search(searchState.term)

              // Set search state with first result focused
              const newState = instanceRef.current.searchState
                .set('results', pspdfkitRef.current.Immutable.List(results))
                .set('focusedResultIndex', 0)
                .set('term', searchState.term)

              instanceRef.current.setSearchState(newState)

              // Jump to first result
              if (results && results.size > 0) {
                const firstResult = results.get(0)
                if (
                  firstResult.pageIndex !== null &&
                  firstResult.rectsOnPage.size > 0
                ) {
                  instanceRef.current.jumpToRect(
                    firstResult.pageIndex,
                    firstResult.rectsOnPage.get(0),
                  )
                }
              }
            }
          },

          clearSearchState: () => {
            if (instanceRef.current && pspdfkitRef.current) {
              const clearState = instanceRef.current.searchState.set(
                'results',
                pspdfkitRef.current.Immutable.List([]),
              )
              instanceRef.current.setSearchState(clearState)
            }
          },

          nextSearchResult: () => {
            const searchState = getValidatedSearchState()
            if (searchState) {
              const nextIndex =
                (searchState.focusedResultIndex + 1) % searchState.results.size
              navigateToResult(nextIndex)
            }
          },

          previousSearchResult: () => {
            const searchState = getValidatedSearchState()
            if (searchState) {
              const currentIndex = searchState.focusedResultIndex
              const prevIndex =
                currentIndex === 0
                  ? searchState.results.size - 1
                  : currentIndex - 1
              navigateToResult(prevIndex)
            }
          },

          getSearchInfo: () => {
            if (instanceRef.current) {
              const searchState = instanceRef.current.searchState
              return {
                resultCount: searchState.results?.size || 0,
                currentIndex: searchState.focusedResultIndex || 0,
                hasResults: (searchState.results?.size || 0) > 0,
                term: searchState.term || '',
              }
            }
            return {
              resultCount: 0,
              currentIndex: 0,
              hasResults: false,
              term: '',
            }
          },

          getSearchResultCount: () => {
            return instanceRef.current?.searchState.results?.size || 0
          },

          getCurrentResultIndex: () => {
            return instanceRef.current?.searchState.focusedResultIndex || 0
          },
        }
      },
      [],
    )

    useEffect(() => {
      const container = containerRef.current
      let PSPDFKit
      ;(async function () {
        PSPDFKit = await import('pspdfkit')
        //This is needed to access the PSPDFKit instance from the component
        //and to use the PSPDFKit methods
        pspdfkitRef.current = PSPDFKit
        const initialViewState = new PSPDFKit.ViewState({
          readOnly,
        })

        const licenseKey = Project.pspdfkitLicenseKey

        const hiddenTools = [
          'sidebar-bookmarks',
          'sidebar-document-outline',
          'sidebar-annotations',
          'pan',
          'signature',
          'stamp',
          'note',
          'text-highlighter',
          'line',
          'polygon',
          'polyline',
          'print',
          'search',
          'export-pdf',
          'highlighter',
          'document-crop',
        ]

        const customTools = [{ type: 'undo' }, { type: 'redo' }]

        const customRotateTools = (instance) => {
          return [
            {
              type: 'custom',
              title: 'Rotate left',
              icon: '/images/icons/tools/rotate_left.svg',
              onPress: () => {
                instance.setViewState((viewState) => viewState.rotateLeft())
              },
            },
            {
              type: 'custom',
              title: 'Rotate right',
              icon: '/images/icons/tools/rotate_right.svg',
              onPress: () => {
                instance.setViewState((viewState) => viewState.rotateRight())
              },
            },
          ]
        }

        await PSPDFKit.load({
          licenseKey,
          container,
          document,
          baseUrl: `${window.location.protocol}//${window.location.host}/`,
          initialViewState,
          autoSaveMode: PSPDFKit.AutoSaveMode.DISABLED,
        }).then(async (instance) => {
          instance.history.enable()

          const rotateTools = customRotateTools(instance)

          const items = instance.toolbarItems
          instance.setToolbarItems([
            ...items.filter((item) => hiddenTools.indexOf(item.type) === -1),
            ...customTools,
            ...rotateTools,
          ])

          const handleAnnotation = () => {
            instance.save()
            instance
              .exportPDF({
                flatten: true,
                includeComments: true,
                saveForPrinting: true,
                excludeAnnotations: false,
              })
              .then((res) => {
                const blob = new Blob([res], { type: 'application/pdf' })
                const setFile = () => {
                  const documentBlobObjectUrl = URL.createObjectURL(blob)
                  const formData = new FormData()
                  formData.append('file', blob)
                  onAnnotate?.(blob, documentBlobObjectUrl)
                }
                setFile()
              })
          }

          instance.addEventListener('annotations.create', async () => {
            handleAnnotation()
          })
          instance.addEventListener('annotations.update', async () => {
            handleAnnotation()
          })
          instance.addEventListener('annotations.change', async () => {
            handleAnnotation()
          })
          instance.addEventListener('document.change', async () => {
            handleAnnotation()
          })

          // Store instance in ref
          instanceRef.current = instance
        })
      })()

      return () => PSPDFKit && PSPDFKit.unload(container)
    }, [document])

    return <Box ref={containerRef} style={{ height: '100%', width: '100%' }} />
  },
)

PspdfkitComponent.displayName = 'PspdfkitComponent'

export default PspdfkitComponent
